import { useRef, useState, type ReactElement, type ReactNode } from "react";
import { ListChecks } from "lucide-react";
import type { SipocRow } from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import { Button } from "../../atoms/Button/Button.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import { Modal } from "../../molecules/Modal/Modal.js";

type SipocFlowEntry = SipocRow["inputs"][number];

export type SipocEntryDetailValues = Pick<
  SipocFlowEntry,
  "requirements" | "notes"
>;

/**
 * One details editor shared by the compact board and expanded SIPOC.
 *
 * A saved requirement is board content, not hidden metadata: it shows as a
 * line under the entry (FEAT-15), clamped so a 2,000-character requirement
 * cannot push the board apart, and that line opens this same dialog to read
 * or edit it in full.
 */
export function SipocEntryDetails({
  entry,
  label,
  readOnly,
  expanded,
  iconClassName,
  onChange,
  children,
}: {
  readonly entry: SipocFlowEntry;
  readonly label: string;
  readonly readOnly: boolean | undefined;
  /** The expanded editor shows four lines of a requirement; the card two. */
  readonly expanded: boolean;
  /** The requirement icon's size, from the board's type scale. */
  readonly iconClassName: string;
  readonly onChange: (details: SipocEntryDetailValues) => void;
  readonly children: ReactNode;
}): ReactElement {
  const [draft, setDraft] = useState<{
    requirements: string;
    notes: string;
  } | null>(null);
  const [baseline, setBaseline] = useState({ requirements: "", notes: "" });
  // Every way in (double-click, the requirement line, the button) opens on
  // Requirements, not the header Close (FEAT-15). Only an editor's field holds
  // the ref, so a reader starts on Close.
  const requirementsRef = useRef<HTMLTextAreaElement>(null);
  const open = (): void => {
    const saved = {
      requirements: entry.requirements ?? "",
      notes: entry.notes ?? "",
    };
    setBaseline(saved);
    setDraft(saved);
  };
  const changedElsewhere =
    draft !== null &&
    ((entry.requirements ?? "") !== baseline.requirements ||
      (entry.notes ?? "") !== baseline.notes);
  const hasChanges =
    draft !== null &&
    (draft.requirements !== baseline.requirements ||
      draft.notes !== baseline.notes);
  const isInput = label.startsWith("Inputs");
  const requirement = (entry.requirements ?? "").trim();
  const trigger =
    requirement !== "" || (entry.notes ?? "").trim() !== ""
      ? "Requirement & notes"
      : readOnly
        ? "Details"
        : "Add requirement";
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- Optional double-click shortcut; the Details button provides keyboard and touch access.
    <div
      onDoubleClick={(event) => {
        // Modal events bubble through React portals; only the entry opens details.
        if (event.currentTarget.contains(event.target as Node)) open();
      }}
    >
      {children}
      {requirement !== "" ? (
        // eslint-disable-next-line ds/no-hand-built -- kept: a multi-line requirement that wraps and clamps under its entry; Button centres one unwrapped line at a fixed height, so no Button size can hold it.
        <button
          type="button"
          onClick={open}
          // Like the card's other controls: without the mousedown stop a press
          // inside the canvas card's drag handle starts a node drag.
          onMouseDown={(event) => event.stopPropagation()}
          aria-label={`Requirement for ${label}: ${requirement}`}
          className="nodrag nopan mt-1 flex w-full items-start gap-1.5 rounded-md px-1 py-0.5 text-start text-xs text-pb-text-secondary hover-quiet press focus-ring"
        >
          <ListChecks
            className={cn("mt-px shrink-0", iconClassName)}
            aria-hidden="true"
          />
          <span
            className={cn(
              "min-w-0 whitespace-pre-wrap break-words",
              expanded ? "line-clamp-4" : "line-clamp-2",
            )}
          >
            {requirement}
          </span>
        </button>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        onClick={open}
        className="nodrag nopan"
        // The name starts with the visible words (label-in-name, WCAG 2.5.3),
        // so a voice user can say what they see.
        aria-label={`${trigger} for ${label}`}
      >
        {trigger}
      </Button>
      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={isInput ? "Input details" : "Output details"}
        description={`${label}: ${entry.text || "Unnamed entry"}`}
        size="lg"
        placement="center"
        initialFocusRef={requirementsRef}
        footer={
          readOnly ? (
            <Button onClick={() => setDraft(null)}>Close details</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button
                disabled={changedElsewhere || !hasChanges}
                onClick={() => {
                  if (!draft || readOnly || changedElsewhere) return;
                  onChange(draft);
                  setDraft(null);
                }}
              >
                Save details
              </Button>
            </>
          )
        }
      >
        <div className="space-y-5">
          {changedElsewhere ? (
            <p
              role="alert"
              className="rounded-lg border border-pb-amber/40 bg-pb-amber/10 px-4 py-3 text-sm text-pb-amber-text"
            >
              These details changed while you were editing. Close and reopen
              them to review the latest version.
            </p>
          ) : null}
          <div className="grid gap-5 sm:grid-cols-2">
            <Textarea
              ref={readOnly ? undefined : requirementsRef}
              label="Requirements"
              aria-label="Requirements"
              hint={
                isInput
                  ? "What must be true before this input is used?"
                  : "What must this output meet for its customer?"
              }
              showCount
              value={draft?.requirements ?? ""}
              maxLength={2000}
              rows={6}
              readOnly={readOnly}
              onChange={(event) =>
                setDraft((current) =>
                  current
                    ? { ...current, requirements: event.target.value }
                    : current,
                )
              }
            />
            <Textarea
              label="Notes"
              aria-label="Notes"
              hint="Add context that helps the next person use this step."
              showCount
              value={draft?.notes ?? ""}
              maxLength={2000}
              rows={6}
              readOnly={readOnly}
              onChange={(event) =>
                setDraft((current) =>
                  current ? { ...current, notes: event.target.value } : current,
                )
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
